import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/utils/axios";

import cls from "./ClubRules.module.scss";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return "—";
  }
};

const ClubRules = () => {
  const { cityId, streetId, clubId } = useParams();

  const [club, setClub] = useState(null);
  const [rules, setRules] = useState([]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const clubName = club?.clubName || "Club";

  const backToClubPath = useMemo(() => {
    if (!cityId || !streetId || !clubId) return "/admin/cities";
    return `/admin/cities/single/${cityId}/streets/${streetId}/clubs/${clubId}`;
  }, [cityId, streetId, clubId]);

  const createRulePath = useMemo(() => {
    if (!cityId || !streetId || !clubId) return "/admin/cities";
    return `/admin/cities/single/${cityId}/streets/${streetId}/clubs/${clubId}/rules/create`;
  }, [cityId, streetId, clubId]);

  const fetchClubMeta = useCallback(async () => {
    if (!cityId || !streetId || !clubId) return;
    try {
      const { data } = await api.get(
        `/v1/admin/cities/${cityId}/streets/${streetId}/clubs/${clubId}`
      );
      setClub(data || null);
    } catch (e) {
      console.error("Failed to load club meta", e);
      // keep page working even if meta fails
      setClub(null);
    }
  }, [cityId, streetId, clubId]);

  const fetchRules = useCallback(async () => {
    if (!cityId || !streetId || !clubId) return;

    try {
      setLoading(true);
      setErr("");

      const { data } = await api.get(
        `/v1/admin/cities/${cityId}/streets/${streetId}/clubs/${clubId}/rules`
      );

      setRules(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load rules", e);
      setErr(
        e?.response?.data?.message || e?.message || "Could not load rules."
      );
      setRules([]);
    } finally {
      setLoading(false);
    }
  }, [cityId, streetId, clubId]);

  const refreshAll = useCallback(async () => {
    await Promise.all([fetchClubMeta(), fetchRules()]);
  }, [fetchClubMeta, fetchRules]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  return (
    <div className={cls.page}>
      <header className={cls.header}>
        <div className={cls.leftHead}>
          <Link className={cls.backLink} to={backToClubPath}>
            ← Back to {clubName}
          </Link>

          <p className={cls.eyebrow}>Admin · Club Rules</p>
          <h1 className={cls.title}>Rules of {clubName}</h1>
          <p className={cls.subtitle}>
            Manage rules, images, and visibility for this club.
          </p>
        </div>

        <div className={cls.actions}>
          <button
            type="button"
            className={cls.ghostBtn}
            onClick={refreshAll}
            disabled={loading}
          >
            Refresh
          </button>

          <Link className={cls.primaryBtn} to={createRulePath}>
            + Create Rule
          </Link>
        </div>
      </header>

      {err && <div className={cls.error}>⚠ {err}</div>}

      <section className={cls.card}>
        <div className={cls.cardTop}>
          <div className={cls.cardTitleRow}>
            <h2 className={cls.cardTitle}>Rules</h2>
            <span className={cls.countPill}>{rules.length}</span>
          </div>
          <p className={cls.cardHint}>
            Tip: Keep inactive rules for drafts or seasonal updates.
          </p>
        </div>

        <div className={cls.tableWrap} aria-busy={loading ? "true" : "false"}>
          <table className={cls.table}>
            <thead>
              <tr>
                <th className={cls.colNum}>#</th>
                <th className={cls.colTitle}>Title</th>
                <th className={cls.colStatus}>Status</th>
                <th className={cls.colUpdated}>Updated</th>
                <th className={cls.colAction}>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                [...Array(6)].map((_, i) => (
                  <tr key={`sk-${i}`} className={cls.skelRow}>
                    <td>
                      <div className={cls.skelLine} />
                    </td>
                    <td>
                      <div className={cls.skelPill} />
                    </td>
                    <td>
                      <div className={cls.skelLineSmall} />
                    </td>
                    <td>
                      <div className={cls.skelBtn} />
                    </td>
                  </tr>
                ))
              ) : rules.length === 0 ? (
                <tr>
                  <td colSpan={4} className={cls.empty}>
                    No rules yet. Click <strong>Create Rule</strong> to add the
                    first one.
                  </td>
                </tr>
              ) : (
                rules.map((r) => (
                  <tr key={r._id} className={cls.row}>
                    <td className={cls.numCell}>{r.sortOrder ?? "—"}</td>

                    <td className={cls.titleCell}>
                      <div className={cls.ruleTitle}>
                        {r.title || "Untitled Rule"}
                      </div>
                      {r.description ? (
                        <div className={cls.ruleSub}>
                          {String(r.description).slice(0, 90)}
                          {String(r.description).length > 90 ? "…" : ""}
                        </div>
                      ) : null}
                    </td>

                    <td>
                      <span className={cls.statusPill} data-status={r.status}>
                        {r.status || "active"}
                      </span>
                    </td>

                    <td className={cls.updatedCell}>
                      {formatDate(r.lastUpdatedAt || r.updatedAt)}
                    </td>

                    <td className={cls.actionCell}>
                      <Link
                        className={cls.viewBtn}
                        to={`/admin/cities/single/${cityId}/streets/${streetId}/clubs/${clubId}/rules/${r._id}`}
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default ClubRules;
